import {
    type ReactNode,
    useContext,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    View
} from "react-native";
import type INavigationMenuCollapsedProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    NavigationMenuLayoutContext,
    NavigationMenuToggleContext,
    NavigationMenuItemContext,
    useNavigationMenuContext,
    NavigationMenuContext
} from "../../context";
import type {
    NavigationMenuItemElementType,
    NavigationMenuItemContextType
} from "../../type";
import {
    Menu as MenuIcon,
    X as XIcon
} from "lucide-react-native";
import {
    getNavigationMenuHasContent,
    getNavigationMenuItemKey
} from "../../util";
import AnimatedIcon from "../../../animatedIcon";
import Accordion from "../../../accordion";
import NavigationMenuLink from "../link";
import Button from "../../../button";
import Sheet from "../../../sheet";
import Text from "../../../text";

const NAVIGATION_MENU_SHEET_LAYOUT = {
    isSheet: true
};

const NavigationMenuToggleIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => {
    const isSheetOpen = useContext(NavigationMenuToggleContext);

    return <AnimatedIcon
        isTransitioned={isSheetOpen}
        customColor={customColor}
        iconTransition="morph"
        fromIcon={MenuIcon}
        toIcon={XIcon}
        size={size}
    />;
};

const NavigationMenuItemProvider: FC<NavigationMenuItemContextType & {
    children: ReactNode;
}> = ({
    anchorRef,
    children,
    value
}) => {
    const itemContext = useMemo(() => ({
        anchorRef,
        value
    }), [
        anchorRef,
        value
    ]);

    return <NavigationMenuItemContext.Provider
        value={itemContext}
    >
        {children}
    </NavigationMenuItemContext.Provider>;
};

const NavigationMenuCollapsed: FC<INavigationMenuCollapsedProps> = ({
    handleSheetClose,
    handleSheetOpen,
    isSheetOpen,
    footer,
    items,
    style,
    title
}) => {
    const context = useNavigationMenuContext({
        part: "Item"
    });

    const customTheme = context?.customTheme;

    const customLocalize = context?.customLocalize;

    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const anchorRef = useRef<View>(null);

    const {
        body: bodyDynamicStyle
    } = useStyles({
        spaces
    });

    if(!context) {
        return null;
    }

    const handleSheetOpenChange = (isNextOpen: boolean) => {
        if(!isNextOpen) {
            handleSheetClose();
        }
    };

    const handleToggle = () => {
        if(isSheetOpen) {
            handleSheetClose();

            return;
        }

        handleSheetOpen();
    };

    const renderItem = (item: NavigationMenuItemElementType) => {
        const {
            isDisabled = false,
            isActive = false,
            children,
            onPress,
            href
        } = item.props;

        const itemKey = getNavigationMenuItemKey(item.props);

        if(!getNavigationMenuHasContent({
            children,
            href
        })) {
            return <View
                key={itemKey}
                role="list"
            >
                <NavigationMenuLink
                    customTheme={customTheme}
                    title={item.props.title}
                    isDisabled={isDisabled}
                    isActive={isActive}
                    onPress={onPress}
                    href={href}
                />
            </View>;
        }

        return <Accordion
            defaultValue={isActive ? itemKey : undefined}
            customTheme={customTheme}
            variant="plain"
            key={itemKey}
            size="large"
        >
            <Accordion.Item
                isDisabled={isDisabled}
                value={itemKey}
            >
                <Accordion.Trigger
                    title={item.props.title}
                />
                <Accordion.Content>
                    <NavigationMenuItemProvider
                        anchorRef={anchorRef}
                        value={itemKey}
                    >
                        {children}
                    </NavigationMenuItemProvider>
                </Accordion.Content>
            </Accordion.Item>
        </Accordion>;
    };

    const renderProviders = (node: ReactNode) => {
        return <NavigationMenuContext.Provider
            value={context}
        >
            <NavigationMenuLayoutContext.Provider
                value={NAVIGATION_MENU_SHEET_LAYOUT}
            >
                {node}
            </NavigationMenuLayoutContext.Provider>
        </NavigationMenuContext.Provider>;
    };

    return <View
        style={[
            style,
            stylesheet.root
        ]}
    >
        <Text
            customTheme={customTheme}
            style={stylesheet.title}
            variant="labelLargeSize"
            numberOfLines={1}
            color="high"
        >
            {title}
        </Text>
        <NavigationMenuToggleContext.Provider
            value={isSheetOpen}
        >
            <Button
                accessibilityLabel={localize(isSheetOpen ? "close-menu" : "open-menu")}
                icon={NavigationMenuToggleIcon}
                aria-expanded={isSheetOpen}
                customTheme={customTheme}
                onPress={handleToggle}
                size={context.size}
                variant="ghost"
                type="neutral"
            />
        </NavigationMenuToggleContext.Provider>
        <Sheet
            onOpenChange={handleSheetOpenChange}
            customLocalize={customLocalize}
            customTheme={customTheme}
            isOpen={isSheetOpen}
            side="top"
        >
            <Sheet.Content
                aria-label={title}
            >
                <Sheet.Header
                    title={title}
                />
                <Sheet.Body>
                    {
                        renderProviders(<View
                            style={[
                                stylesheet.body,
                                bodyDynamicStyle
                            ]}
                            aria-label={title}
                            role="navigation"
                        >
                            {items.map(renderItem)}
                        </View>)
                    }
                </Sheet.Body>
                {
                    footer ?
                        <Sheet.Footer
                            align="stretch"
                        >
                            {footer}
                        </Sheet.Footer>
                    :
                        null
                }
            </Sheet.Content>
        </Sheet>
    </View>;
};
export default NavigationMenuCollapsed;
